import React, {
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import EnrollmentLogsModal from "../components/EnrollmentLogsModal";
import StudentForm from "../components/StudentForm";
import EditStudentForm from "../components/EditStudentForm";
import PaymentForm from "../components/PaymentForm";
import Sidebar from "../components/Sidebar";
import ActionModal from "../components/ActionModal";

import {
  getStudents,
  getStudent,
  createStudent,
  updateStudent,
  deleteStudent,
  grantGrace,
  banEnrollment,
  unbanEnrollment,
  revokeGrace,
  removeEnrollment,
  addEnrollment,
  resendAccessEmail,
  resendDriveAccess,
} from "../api/students";
import { getCourses } from "../api/courses";
import { createPayment } from "../api/payments";

import {
  Search,
  Eye,
  Pencil,
  Trash2,
  Ban,
  Check,
  Plus,
  Mail,
  Phone,
  Download,
  ExternalLink,
  Gift,
  XCircle,
  Star,
  X,
  IndianRupee,
  Copy,
  Users as UsersIcon,
  HardDrive,
  MoreVertical,
  Smartphone,
  ShieldAlert,
} from "lucide-react";

import type {
  Student,
  Enrollment,
  CreateStudentRequest,
  UpdateStudentRequest,
} from "../types/student";
import type { CreatePaymentRequest } from "../types/payment";
import { generatePaginationItems } from "../utils/pagination";
import { mobileAccountApi } from "../features/mobileAdmin/accounts/api";
import type { MobileActivationEmailPreview } from "../features/mobileAdmin/accounts/types";
import DeviceChangeActivityModal from "../features/mobileAdmin/devices/DeviceChangeActivityModal";

export default function Students() {
  const [showFilter, setShowFilter] = useState(false);
  const { user } = useContext(AuthContext);
  const nav = useNavigate();

  // Data & loading
  const [students, setStudents] = useState<Student[]>([]);
  const [coursesOptions, setCoursesOptions] = useState<
    { id: string; name: string }[]
  >([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [hoveredStudentId, setHoveredStudentId] = useState<string | null>(null);

  // Global action loader
  const [actionLoading, setActionLoading] = useState<boolean>(false);
  const [showMobileBulk, setShowMobileBulk] = useState(false);
  const [showDeviceActivity, setShowDeviceActivity] = useState(false);
  const [mobileBulkCourseId, setMobileBulkCourseId] = useState("");
  const [mobileBulkPreview, setMobileBulkPreview] = useState<MobileActivationEmailPreview | null>(null);
  const [mobileBulkLoading, setMobileBulkLoading] = useState(false);
  const [mobileBulkError, setMobileBulkError] = useState("");
  const [countdownNow, setCountdownNow] = useState(Date.now());

  // Search + Filters
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [courseFilter, setCourseFilter] = useState<string>("all");
  const [paymentFilter, setPaymentFilter] = useState<
    "all" | "unpaid" | "partial" | "paid"
  >("all");
  const [accessFilter, setAccessFilter] = useState<
    "all" | "full" | "grace" | "none"
  >("all");
  const [courseStatusFilter, setCourseStatusFilter] = useState<string>("all");

  // ★ Star filter
  const [starFilter, setStarFilter] = useState<"all" | "starred">("all");
  // ★ Archive filter: show all / only active / only archived courses
  const [archiveFilter, setArchiveFilter] = useState<
    "all" | "active" | "archived"
  >("all");

  // Enrollment Date Range Filter State
  const [enrollDateFrom, setEnrollDateFrom] = useState<string>("");
  const [enrollDateTo, setEnrollDateTo] = useState<string>("");

  // Pagination
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(5);

  // Modals & targets
  const [showNew, setShowNew] = useState(false);
  const [editTarget, setEditTarget] = useState<{
    student: Student;
    initialStep?: 1 | 2 | 3 | 4;
  } | null>(null);
  const [paymentTarget, setPaymentTarget] = useState<{
    studentId: string;
    courseId: string;
    remaining: number;
  } | null>(null);
  const [logModal, setLogModal] = useState<{
    studentId: string;
    enrollmentId: string;
    courseName: string;
  } | null>(null);
  const [actionModal, setActionModal] = useState<{
    type: "grantGrace" | "ban";
    studentId: string;
  } | null>(null);
  const [modalEnrollmentId, setModalEnrollmentId] = useState<string>("");
  const [modalGraceDays, setModalGraceDays] = useState<number>(7);

  const [openActionMenu, setOpenActionMenu] = useState<{
    studentId: string;
    enrollmentId: string;
    isPrimaryRow: boolean;
    top: number;
    left: number;
  } | null>(null);

  useEffect(() => {
    if (!openActionMenu) return;

    const closeMenu = () => setOpenActionMenu(null);

    window.addEventListener("resize", closeMenu);
    window.addEventListener("scroll", closeMenu, true);

    return () => {
      window.removeEventListener("resize", closeMenu);
      window.removeEventListener("scroll", closeMenu, true);
    };
  }, [openActionMenu]);

  // Permissions (unchanged)
  const canEdit = ["superadmin", "admin", "viewer"].includes(user?.role || "");
  const canModifyEnrollments = ["superadmin", "admin"].includes(
    user?.role || "",
  );
  const canCreate = true;

  // Check if user can resend email for a student
  const canResendEmail = (s: Student) => {
    if (user?.role === "superadmin" || user?.role === "admin") return true;
    if (user?.role === "viewer" && s.createdBy?.id === user.id) return true;
    return false;
  };

  // Helper: sort students by createdAt descending
  const sortByNewest = (list: Student[]) =>
    list
      .slice()
      .sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      );

  // Load data on mount
  useEffect(() => {
    let mounted = true;
    (async () => {
      const [studs, courses] = await Promise.all([getStudents(), getCourses()]);
      if (!mounted) return;
      setStudents(sortByNewest(studs));
      setCoursesOptions(courses.map((c) => ({ id: c.id, name: c.name })));
      setLoading(false);
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // Silent reload — keeps the table visible (no full-page spinner)
  const reload = async () => {
    const studs = await getStudents();
    setStudents(sortByNewest(studs));
  };

  // Replace a single student in state using the object the server already returned
  const replaceStudent = (updated: Student) =>
    setStudents((prev) =>
      sortByNewest(prev.map((s) => (s.id === updated.id ? updated : s))),
    );

  // Stable enrollment-level filter — also used by the render to filter displayed enrollments
  const matchesFilters = useCallback(
    (en: Enrollment) => {
      if (archiveFilter === "active" && en.course.isArchived) return false;
      if (archiveFilter === "archived" && !en.course.isArchived) return false;

      if (courseFilter !== "all" && en.course.id !== courseFilter) return false;
      if (paymentFilter === "paid" && !en.isFullyPaid) return false;
      if (paymentFilter === "partial" && !(en.paidStatus && !en.isFullyPaid))
        return false;
      if (paymentFilter === "unpaid" && (en.paidStatus || en.isFullyPaid))
        return false;
      if (accessFilter === "full" && en.status !== "access_granted")
        return false;
      if (accessFilter === "grace" && en.status !== "grace_used") return false;
      if (accessFilter === "none" && en.status !== "no_access") return false;

      if (courseStatusFilter !== "all") {
        const todayStr = new Date().toDateString();
        if (courseStatusFilter === "active") {
          if (
            en.endDate &&
            new Date(todayStr) > new Date(new Date(en.endDate).toDateString())
          )
            return false;
        }
        if (courseStatusFilter === "completed") {
          if (!en.endDate) return false;
          if (
            new Date(todayStr) <= new Date(new Date(en.endDate).toDateString())
          )
            return false;
        }
      }

      if (
        enrollDateFrom &&
        (!en.enrollDate || new Date(en.enrollDate) < new Date(enrollDateFrom))
      )
        return false;
      if (
        enrollDateTo &&
        (!en.enrollDate || new Date(en.enrollDate) > new Date(enrollDateTo))
      )
        return false;
      return true;
    },
    [
      archiveFilter,
      courseFilter,
      paymentFilter,
      accessFilter,
      courseStatusFilter,
      enrollDateFrom,
      enrollDateTo,
    ],
  );

  // Memoized student list — only recomputes when students or a filter value changes
  const filteredStudents = useMemo(() => {
    return students.filter((s) => {
      if (starFilter === "starred" && !s.isTopper) return false;
      const q = searchTerm.toLowerCase();
      if (
        !(
          s.name.toLowerCase().includes(q) ||
          s.email.toLowerCase().includes(q) ||
          s.id.toLowerCase().includes(q)
        )
      )
        return false;
      return s.enrollments.some(matchesFilters);
    });
  }, [students, searchTerm, starFilter, matchesFilters]);

  // Pagination (unchanged)
  const totalPages = Math.ceil(filteredStudents.length / rowsPerPage);
  const displayed = filteredStudents.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage,
  );
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [totalPages, currentPage]);
  const startIndex = (currentPage - 1) * rowsPerPage;

  // CRUD & helpers (unchanged)
  const handleNew = async (data: CreateStudentRequest | FormData) => {
    setActionLoading(true);
    try {
      const created = await createStudent(data as any);
      setStudents((prev) => sortByNewest([...prev, created]));
      setShowNew(false);
    } finally {
      setActionLoading(false);
    }
  };
  const handleEdit = async (
    id: string,
    data: UpdateStudentRequest | FormData,
  ) => {
    setActionLoading(true);
    try {
      const updated = await updateStudent(id, data as any);
      replaceStudent(updated);
      setEditTarget(null);
    } finally {
      setActionLoading(false);
    }
  };
  function canDeleteStudent(s: Student) {
    if (user?.role === "superadmin") return true;
    if (
      (user?.role === "admin" || user?.role === "viewer") &&
      s.createdBy?.id === user.id
    ) {
      return (
        Date.now() - new Date(s.createdAt).getTime() <= 24 * 60 * 60 * 1000
      );
    }
    return false;
  }
  const handleDeleteStudent = async (id: string) => {
    if (
      !window.confirm(
        "Delete this student, all enrollments, and their mobile account? This permanently revokes mobile sessions, registered devices, and content access.",
      )
    )
      return;
    setActionLoading(true);
    try {
      await deleteStudent(id);
      setStudents((prev) => prev.filter((s) => s.id !== id));
    } finally {
      setActionLoading(false);
    }
  };

  // Grace/Ban per-enrollment (unchanged)
  const handleGrantGraceClick = (sid: string, eid: string) => {
    setModalEnrollmentId(eid);
    setModalGraceDays(7);
    setActionModal({ type: "grantGrace", studentId: sid });
  };
  const handleBanClick = (sid: string, eid: string) => {
    setModalEnrollmentId(eid);
    setActionModal({ type: "ban", studentId: sid });
  };
  const handleRevokeGraceClick = async (sid: string, eid: string) => {
    if (!window.confirm("Revoke grace period?")) return;
    setActionLoading(true);
    try {
      const updated = await revokeGrace(sid, eid);
      replaceStudent(updated);
    } finally {
      setActionLoading(false);
    }
  };
  const handleUnbanClick = async (sid: string, eid: string) => {
    if (!window.confirm("Unban this enrollment?")) return;
    setActionLoading(true);
    try {
      const updated = await unbanEnrollment(sid, eid);
      replaceStudent(updated);
    } finally {
      setActionLoading(false);
    }
  };
  const handleRemoveEnrollment = async (sid: string, eid: string) => {
    if (!window.confirm("Remove this enrollment?")) return;
    setActionLoading(true);
    try {
      const updated = await removeEnrollment(sid, eid);
      replaceStudent(updated);
    } finally {
      setActionLoading(false);
    }
  };

  const handleResendEmail = async (sid: string, eid: string) => {
    if (!window.confirm("Resend access email with invite link?")) return;
    setActionLoading(true);
    try {
      const result = await resendAccessEmail(sid, eid);
      alert(result.message || "Email sent successfully!");
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || "Failed to send email";
      alert(errorMsg);
    } finally {
      setActionLoading(false);
    }
  };

  const handleResendDrive = async (sid: string, eid: string) => {
    if (!window.confirm("Retrigger Google Drive access?")) return;
    setActionLoading(true);
    try {
      const result = await resendDriveAccess(sid, eid);
      alert(result.message || "Google Drive access retriggered successfully!");
    } catch (error: any) {
      const errorMsg =
        error.response?.data?.message ||
        "Failed to retrigger Google Drive access";
      alert(errorMsg);
    } finally {
      setActionLoading(false);
    }
  };

  const handleActivateMobileAccount = async (student: Student) => {
    if (
      !window.confirm(
        `Create a mobile account for ${student.name} and email the activation OTP to ${student.email}?`,
      )
    )
      return;

    setActionLoading(true);
    try {
      const response = await mobileAccountApi.create(student.id);
      const activation = response.data;

      setStudents((current) =>
        current.map((item) =>
          item.id === student.id
            ? { ...item, mobileAccountStatus: activation.account.status }
            : item,
        ),
      );

      if (activation.activationEmailSent) {
        alert(
          `Mobile activation created. The OTP email was sent to ${student.email}.`,
        );
      } else {
        alert(
          `The mobile account was created, but the email could not be sent. Activation OTP: ${activation.activationToken}\n\nOpen Mobile Account Setup if you need to manage this activation.`,
        );
      }
    } catch (error: any) {
      alert(
        error?.message ||
          "The mobile account could not be activated. Please try again.",
      );
    } finally {
      setActionLoading(false);
    }
  };

  useEffect(() => {
    if (!showMobileBulk) return;
    const timer = window.setInterval(() => setCountdownNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [showMobileBulk]);

  const loadMobileBulkPreview = async (courseId: string) => {
    setMobileBulkCourseId(courseId);
    setMobileBulkPreview(null);
    setMobileBulkError("");
    if (!courseId) return;
    setMobileBulkLoading(true);
    try {
      const response = await mobileAccountApi.bulkActivationPreview(courseId);
      setMobileBulkPreview(response.data);
    } catch (error: any) {
      setMobileBulkError(error?.message || "Could not load mobile activation details.");
    } finally {
      setMobileBulkLoading(false);
    }
  };

  const handleMobileBulkSend = async () => {
    if (!mobileBulkCourseId || !mobileBulkPreview?.willSend) return;
    setMobileBulkLoading(true);
    setMobileBulkError("");
    try {
      const response = await mobileAccountApi.sendBulkActivation(mobileBulkCourseId);
      const result = response.data;
      alert(`Mobile activation emails sent: ${result.sent}${result.failed ? `\nFailed: ${result.failed}` : ""}${result.skipped ? `\nAlready handled today: ${result.skipped}` : ""}`);
      await loadMobileBulkPreview(mobileBulkCourseId);
      await reload();
    } catch (error: any) {
      setMobileBulkError(error?.message || "Could not send mobile activation emails.");
      setMobileBulkLoading(false);
    }
  };

  const mobileResetCountdown = useMemo(() => {
    if (!mobileBulkPreview) return "";
    const seconds = Math.max(0, Math.floor((new Date(mobileBulkPreview.nextResetAt).getTime() - countdownNow) / 1000));
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours}h ${minutes}m ${secs}s`;
  }, [mobileBulkPreview, countdownNow]);

  // Payments & logs (unchanged)
  const handlePaymentSubmit = async (data: CreatePaymentRequest) => {
    setActionLoading(true);
    try {
      await createPayment(data);
      // Payment changes financials — refresh just this one student
      const updated = await getStudent(data.student);
      replaceStudent(updated);
      setPaymentTarget(null);
    } finally {
      setActionLoading(false);
    }
  };
  const handleShowLogs = (
    studentId: string,
    enrollmentId: string,
    courseName: string,
  ) => {
    setLogModal({ studentId, enrollmentId, courseName });
  };
  const confirmAction = async (graceDays?: number) => {
    if (!actionModal) return;
    setActionLoading(true);
    try {
      let updated: Student;
      if (actionModal.type === "grantGrace") {
        updated = await grantGrace(
          actionModal.studentId,
          modalEnrollmentId,
          graceDays || modalGraceDays,
        );
      } else {
        updated = await banEnrollment(actionModal.studentId, modalEnrollmentId);
      }
      replaceStudent(updated);
      setActionModal(null);
    } finally {
      setActionLoading(false);
    }
  };

  // Export the currently filtered student list.
  const handleExport = () => {
    const toExport = filteredStudents;
    const header = ["ID", "Name", "Email", "Phone"].join(",");
    const rows = toExport
      .map((s) =>
        [s.id, s.name, s.email, s.phone]
          .map((f) => `"${String(f).replace(/"/g, '""')}"`)
          .join(","),
      )
      .join("\n");
    const blob = new Blob([`${header}\n${rows}`], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "students_export.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const fieldClass = `
        box-border h-10 min-w-0 w-full max-w-full rounded-[9px] border border-slate-200 bg-white px-3
        text-[12px] font-medium text-slate-800 outline-none transition
        placeholder:text-slate-400 hover:border-slate-300
        focus:border-slate-400 focus:ring-2 focus:ring-slate-100
    `;

  const iconButtonClass = `
        inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg
        border border-emerald-200 bg-white p-0 text-emerald-700 transition
        hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-800
        focus:outline-none focus:ring-2 focus:ring-emerald-200
        disabled:cursor-not-allowed disabled:opacity-40
    `;

  // Explicit border/background resets keep dropdown actions clean even if global button CSS exists.
  const menuItemClass = `
        flex w-full items-center gap-3 rounded-lg !border-0 !bg-transparent px-3 py-2.5
        text-left text-[14px] font-medium text-slate-700 !shadow-none !outline-none transition
        hover:!bg-slate-50 hover:text-slate-950 focus:!bg-slate-50 focus:outline-none
        disabled:cursor-not-allowed disabled:opacity-40
    `;

  const menuItemDangerClass = `${menuItemClass} text-red-600 hover:!bg-red-50 hover:text-red-700`;
  const menuItemSuccessClass = `${menuItemClass} text-emerald-700 hover:!bg-emerald-50 hover:text-emerald-800`;
  const menuItemWarningClass = `${menuItemClass} text-amber-700 hover:!bg-amber-50 hover:text-amber-800`;

  const clearFilters = () => {
    setSearchTerm("");
    setCourseFilter("all");
    setPaymentFilter("all");
    setAccessFilter("all");
    setCourseStatusFilter("all");
    setStarFilter("all");
    setArchiveFilter("all");
    setEnrollDateFrom("");
    setEnrollDateTo("");
  };

  const actionMenuStudent = openActionMenu
    ? students.find((student) => student.id === openActionMenu.studentId)
    : undefined;

  const actionMenuEnrollment =
    actionMenuStudent && openActionMenu
      ? actionMenuStudent.enrollments.find(
          (enrollment) => enrollment.id === openActionMenu.enrollmentId,
        )
      : undefined;

  const actionMenuHasMultipleEnrollments =
    (actionMenuStudent?.enrollments.length || 0) > 1;

  const openMoreActions = (
    event: React.MouseEvent<HTMLButtonElement>,
    studentId: string,
    enrollmentId: string,
    isPrimaryRow: boolean,
  ) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const menuWidth = 238;
    const estimatedHeight = 360;
    const gap = 8;

    const left = Math.max(
      12,
      Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 12),
    );
    const preferredTop = rect.bottom + gap;
    const top =
      preferredTop + estimatedHeight > window.innerHeight
        ? Math.max(12, rect.top - estimatedHeight - gap)
        : preferredTop;

    setOpenActionMenu({ studentId, enrollmentId, isPrimaryRow, top, left });
  };

  if (!user) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-[#f7f8fb]">
      <Sidebar />

      {actionLoading && (
        <div className="fixed inset-0 z-[1300] flex items-center justify-center bg-white/65 backdrop-blur-[1px]">
          <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-[0_12px_40px_rgba(15,23,42,0.12)]">
            <span className="h-5 w-5 animate-spin rounded-full border-2 border-slate-200 border-t-slate-800" />
            <span className="text-[11px] font-semibold text-slate-600">
              Processing…
            </span>
          </div>
        </div>
      )}

      <main
        className="
                    ml-[280px] min-w-0 px-6 py-6 transition-[margin] duration-200
                    [.sidebar-collapsed_&]:ml-[76px]
                    max-lg:ml-[76px] max-lg:px-4 max-sm:px-3
                "
      >
        <div className="mx-auto w-full max-w-[1600px]">
          {/* Header */}
          <header className="mb-5 flex items-start justify-between gap-4 max-md:flex-col max-md:items-stretch">
            <div className="flex items-start gap-3">
              <div className="grid h-10 w-10 shrink-0 place-items-center rounded-[10px] bg-slate-100 text-slate-600">
                <UsersIcon size={20} strokeWidth={1.8} />
              </div>

              <div>
                <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.12em] text-blue-600">
                  Student management
                </div>
                <h1 className="m-0 text-[28px] font-bold tracking-[-0.03em] text-slate-950 max-sm:text-[24px]">
                  Students
                </h1>
                <p className="mb-0 mt-1 text-[12px] leading-5 text-slate-500">
                  Manage student enrollments, access and payments.
                </p>
              </div>
            </div>

            <div className="flex shrink-0 items-center gap-2 max-sm:grid max-sm:grid-cols-2">
              {canModifyEnrollments && (
                <button
                  type="button"
                  onClick={() => setShowDeviceActivity(true)}
                  className="inline-flex h-10 items-center justify-center gap-2 rounded-[9px] border border-amber-200 bg-white px-4 text-[11px] font-semibold text-amber-700 transition hover:border-amber-300 hover:bg-amber-50 focus:outline-none focus:ring-2 focus:ring-amber-200"
                >
                  <ShieldAlert size={15} />
                  Device activity
                </button>
              )}
              {canModifyEnrollments && (
                <button
                  type="button"
                  onClick={() => setShowMobileBulk(true)}
                  className="inline-flex h-10 items-center justify-center gap-2 rounded-[9px] border border-emerald-200 bg-white px-4 text-[11px] font-semibold text-emerald-700 transition hover:border-emerald-300 hover:bg-emerald-50 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                >
                  <Smartphone size={15} />
                  Mobile device
                </button>
              )}
              {user.role !== "viewer" && (
                <button
                  type="button"
                  onClick={handleExport}
                  className="
                                        inline-flex h-10 items-center justify-center gap-2 rounded-[9px]
                                        border border-emerald-200 bg-white px-4 text-[11px] font-semibold
                                        text-emerald-700 transition hover:border-emerald-300 hover:bg-emerald-50
                                        hover:text-emerald-800 focus:outline-none focus:ring-2 focus:ring-emerald-200
                                    "
                >
                  <Download size={15} />
                  Export
                </button>
              )}

              <button
                type="button"
                onClick={() => setShowNew(true)}
                className="
                                    inline-flex h-10 items-center justify-center gap-2 rounded-[9px]
                                    border border-emerald-600 bg-emerald-600 px-4 text-[11px]
                                    font-semibold text-white transition hover:border-emerald-700 hover:bg-emerald-700
                                    focus:outline-none focus:ring-2 focus:ring-emerald-200
                                "
              >
                <Plus size={15} />
                Enroll student
              </button>
            </div>
          </header>
          <DeviceChangeActivityModal open={showDeviceActivity} onClose={() => setShowDeviceActivity(false)} />

          {/* Search + Filters */}
          <section className="mb-4 rounded-[16px] border border-slate-200 bg-white p-4 shadow-[0_1px_3px_rgba(15,23,42,0.04)]">
            <div className="flex flex-col gap-3 md:flex-row md:items-end md:gap-4">
              <label className="min-w-0 flex-1">
                <span className="mb-1.5 block text-[10px] font-semibold uppercase tracking-[0.04em] text-slate-400">
                  Search students
                </span>
                <div className="relative">
                  <Search
                    size={16}
                    className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                  />
                  <input
                    type="text"
                    aria-label="Search students"
                    placeholder="Search by name, email or ID"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className={`${fieldClass} pl-9`}
                  />
                </div>
              </label>

              <div className="flex shrink-0 items-end md:w-[124px]">
                <button
                  type="button"
                  onClick={() => setShowFilter((current) => !current)}
                  className={`
                                        inline-flex h-10 w-full items-center justify-center gap-2 rounded-[9px]
                                        border px-4 text-[11px] font-semibold transition focus:outline-none
                                        focus:ring-2 focus:ring-emerald-200 md:w-full
                                        ${
                                          showFilter
                                            ? "border-emerald-600 bg-emerald-600 text-white hover:border-emerald-700 hover:bg-emerald-700"
                                            : "border-emerald-200 bg-white text-emerald-700 hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-800"
                                        }
                                    `}
                >
                  <Search size={14} />
                  {showFilter ? "Hide filters" : "Filters"}
                </button>
              </div>
            </div>

            {showFilter && (
              <div className="mt-4 border-t border-slate-100 pt-4">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-[repeat(4,minmax(0,1fr))]">
                  <label className="min-w-0">
                    <span className="mb-1.5 block text-[10px] font-semibold uppercase tracking-[0.04em] text-slate-400">
                      Course
                    </span>
                    <select
                      value={courseFilter}
                      onChange={(e) => setCourseFilter(e.target.value)}
                      className={fieldClass}
                    >
                      <option value="all">All courses</option>
                      {coursesOptions.map((course) => (
                        <option key={course.id} value={course.id}>
                          {course.name}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="min-w-0">
                    <span className="mb-1.5 block text-[10px] font-semibold uppercase tracking-[0.04em] text-slate-400">
                      Payment
                    </span>
                    <select
                      value={paymentFilter}
                      onChange={(e) =>
                        setPaymentFilter(e.target.value as typeof paymentFilter)
                      }
                      className={fieldClass}
                    >
                      <option value="all">All payments</option>
                      <option value="paid">Fully paid</option>
                      <option value="partial">Partial</option>
                      <option value="unpaid">Unpaid</option>
                    </select>
                  </label>

                  <label className="min-w-0">
                    <span className="mb-1.5 block text-[10px] font-semibold uppercase tracking-[0.04em] text-slate-400">
                      Access
                    </span>
                    <select
                      value={accessFilter}
                      onChange={(e) =>
                        setAccessFilter(e.target.value as typeof accessFilter)
                      }
                      className={fieldClass}
                    >
                      <option value="all">All access</option>
                      <option value="full">Full access</option>
                      <option value="grace">Grace period</option>
                      <option value="none">No access</option>
                    </select>
                  </label>

                  <label className="min-w-0">
                    <span className="mb-1.5 block text-[10px] font-semibold uppercase tracking-[0.04em] text-slate-400">
                      Course status
                    </span>
                    <select
                      value={courseStatusFilter}
                      onChange={(e) => setCourseStatusFilter(e.target.value)}
                      className={fieldClass}
                    >
                      <option value="all">All statuses</option>
                      <option value="active">Active</option>
                      <option value="completed">Completed</option>
                    </select>
                  </label>

                  <label className="min-w-0">
                    <span className="mb-1.5 block text-[10px] font-semibold uppercase tracking-[0.04em] text-slate-400">
                      Student
                    </span>
                    <select
                      value={starFilter}
                      onChange={(e) =>
                        setStarFilter(e.target.value as typeof starFilter)
                      }
                      className={fieldClass}
                    >
                      <option value="all">All students</option>
                      <option value="starred">Starred only</option>
                    </select>
                  </label>

                  <label className="min-w-0">
                    <span className="mb-1.5 block text-[10px] font-semibold uppercase tracking-[0.04em] text-slate-400">
                      Course archive
                    </span>
                    <select
                      value={archiveFilter}
                      onChange={(e) =>
                        setArchiveFilter(e.target.value as typeof archiveFilter)
                      }
                      className={fieldClass}
                    >
                      <option value="all">All courses</option>
                      <option value="active">Active only</option>
                      <option value="archived">Archived only</option>
                    </select>
                  </label>

                  <label className="min-w-0">
                    <span className="mb-1.5 block text-[10px] font-semibold uppercase tracking-[0.04em] text-slate-400">
                      Enrolled from
                    </span>
                    <input
                      type="date"
                      value={enrollDateFrom}
                      onChange={(e) => setEnrollDateFrom(e.target.value)}
                      className={`${fieldClass} min-w-0 w-full max-w-full overflow-hidden`}
                    />
                  </label>

                  <label className="min-w-0">
                    <span className="mb-1.5 block text-[10px] font-semibold uppercase tracking-[0.04em] text-slate-400">
                      Enrolled to
                    </span>
                    <input
                      type="date"
                      value={enrollDateTo}
                      onChange={(e) => setEnrollDateTo(e.target.value)}
                      className={`${fieldClass} min-w-0 w-full max-w-full overflow-hidden`}
                    />
                  </label>
                </div>

                <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-3">
                  <span className="text-[10px] text-slate-400">
                    Filters apply to individual enrollments.
                  </span>
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="
                                            inline-flex h-8 items-center justify-center gap-1.5 rounded-lg
                                            border border-emerald-200 bg-white px-3 text-[13px] font-semibold
                                            text-emerald-700 transition hover:border-emerald-300 hover:bg-emerald-50
                                            hover:text-emerald-800
                                        "
                  >
                    <X size={13} />
                    Clear filters
                  </button>
                </div>
              </div>
            )}
          </section>

          {loading ? (
            <section className="flex min-h-[260px] flex-col items-center justify-center rounded-[16px] border border-slate-200 bg-white shadow-[0_1px_3px_rgba(15,23,42,0.04)]">
              <span className="h-6 w-6 animate-spin rounded-full border-[3px] border-slate-200 border-t-slate-700" />
              <span className="mt-3 text-[11px] font-medium text-slate-500">
                Loading students…
              </span>
            </section>
          ) : (
            <section className="overflow-hidden rounded-[16px] border border-slate-200 bg-white shadow-[0_1px_3px_rgba(15,23,42,0.04)]">
              <div className="flex items-center justify-between gap-4 border-b border-slate-100 px-5 py-4 max-sm:flex-col max-sm:items-start max-sm:px-4">
                <div>
                  <h2 className="m-0 text-[18px] font-bold text-slate-950">
                    Students
                  </h2>
                  <p className="mb-0 mt-1 text-[13px] text-slate-500">
                    Showing{" "}
                    <strong className="font-semibold text-slate-600">
                      {filteredStudents.length}
                    </strong>{" "}
                    of{" "}
                    <strong className="font-semibold text-slate-600">
                      {students.length}
                    </strong>{" "}
                    student{students.length === 1 ? "" : "s"}
                  </p>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <div className="rounded-lg bg-slate-50 px-3 py-2 text-[13px] text-slate-500">
                    <strong className="font-semibold text-slate-800">
                      {displayed.length}
                    </strong>{" "}
                    on this page
                  </div>
                </div>
              </div>

              {displayed.length > 0 ? (
                <>
                  <div className="w-full overflow-x-auto">
                    <table className="w-full min-w-[1180px] border-collapse">
                      <thead className="bg-slate-50/80">
                        <tr>
                          {[
                            "Student",
                            "Contact",
                            "Course",
                            "Payment",
                            "Enrolled on",
                            "Access till",
                          ].map((label) => (
                            <th
                              key={label}
                              className="px-4 py-3.5 text-left text-[14px] font-semibold uppercase tracking-[0.04em] text-slate-600"
                            >
                              {label}
                            </th>
                          ))}

                          <th className="w-[150px] px-4 py-3.5 text-center text-[14px] font-semibold uppercase tracking-[0.04em] text-slate-600">
                            Actions
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        {displayed.flatMap((student, studentIndex) => {
                          const filtered =
                            student.enrollments.filter(matchesFilters);
                          const multiple = student.enrollments.length > 1;

                          return filtered.map((enrollment, enrollmentIndex) => {
                            const today = new Date();
                            let courseStatus = "Active";

                            if (
                              enrollment.endDate &&
                              new Date(today.toDateString()) >
                                new Date(
                                  new Date(enrollment.endDate).toDateString(),
                                )
                            ) {
                              courseStatus = "Completed";
                            }

                            const accessDate =
                              enrollment.channelAccessEnd?.slice(0, 10);

                            const accessLabel =
                              enrollment.status === "access_granted"
                                ? "Full access"
                                : enrollment.status === "grace_used"
                                  ? `Grace − ${enrollment.graceCount}`
                                  : "No access";

                            const accessClass =
                              enrollment.status === "access_granted"
                                ? "bg-emerald-50 text-emerald-700"
                                : enrollment.status === "grace_used"
                                  ? "bg-amber-50 text-amber-700"
                                  : "bg-red-50 text-red-700";

                            const paymentClass = enrollment.isFullyPaid
                              ? "bg-emerald-50 text-emerald-700"
                              : enrollment.paidStatus
                                ? "bg-amber-50 text-amber-700"
                                : "bg-red-50 text-red-700";

                            const paymentLabel = enrollment.isFullyPaid
                              ? "Paid"
                              : enrollment.paidStatus
                                ? "Partial"
                                : "Unpaid";

                            return (
                              <tr
                                key={`${student.id}-${enrollment.id}`}
                                onMouseEnter={() => setHoveredStudentId(student.id)}
                                onMouseLeave={() => setHoveredStudentId(null)}
                                className={`
                                  border-b border-slate-100 transition-colors
                                  ${
                                    hoveredStudentId === student.id
                                      ? "bg-slate-100/80"
                                      : studentIndex % 2 === 1
                                        ? "bg-slate-50/50"
                                        : "bg-white"
                                  }
                                  ${enrollmentIndex === 0 ? "border-t border-t-slate-300" : ""}
                                  ${enrollmentIndex === filtered.length - 1 ? "border-b border-b-slate-300" : ""}
                                `}
                              >
                                {enrollmentIndex === 0 && (
                                  <>
                                    <td
                                      rowSpan={filtered.length}
                                      className="border-l border-l-slate-300 px-4 py-4 align-middle"
                                    >
                                      <div className="flex min-w-[220px] items-center gap-3">
                                        <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-slate-100 text-slate-600">
                                          <UsersIcon
                                            size={18}
                                            strokeWidth={1.8}
                                          />
                                        </div>

                                        <div className="min-w-0">
                                          <div className="flex items-center gap-1.5">
                                            <strong className="block max-w-[210px] truncate text-[16px] font-semibold text-slate-950">
                                              {student.name}
                                            </strong>

                                            {student.isTopper && (
                                              <Star
                                                size={14}
                                                strokeWidth={1.8}
                                                aria-label="Topper"
                                                className="shrink-0 fill-amber-400 text-amber-400"
                                              />
                                            )}
                                          </div>

                                          <code
                                            title={enrollment.id}
                                            className="mt-1 block max-w-[220px] truncate text-[13px] text-slate-500"
                                          >
                                            {enrollment.id}
                                          </code>

                                          <div
                                            className="mt-1 max-w-[220px] truncate text-[13px] text-slate-500"
                                            title={
                                              student.telegramMemberId ||
                                              undefined
                                            }
                                          >
                                            Telegram ID:{" "}
                                            {student.telegramMemberId || "—"}
                                          </div>
                                        </div>
                                      </div>
                                    </td>

                                    <td
                                      rowSpan={filtered.length}
                                      className="px-4 py-4 align-middle"
                                    >
                                      <div className="min-w-[210px] space-y-1.5">
                                        <a
                                          href={`mailto:${student.email}`}
                                          className="flex max-w-[240px] items-center gap-1.5 truncate text-[15px] text-slate-700 no-underline hover:text-slate-950 hover:underline"
                                        >
                                          <Mail
                                            size={13}
                                            className="shrink-0 text-slate-400"
                                          />
                                          <span className="truncate">
                                            {student.email}
                                          </span>
                                        </a>

                                        <div className="flex items-center gap-1.5 text-[14px] text-slate-600">
                                          <Phone size={13} />
                                          {student.phone || "—"}
                                        </div>
                                      </div>
                                    </td>
                                  </>
                                )}

                                <td className="px-4 py-4 align-middle">
                                  <div className="min-w-[150px]">
                                    <strong
                                      className="block max-w-[190px] truncate text-[15px] font-semibold text-slate-900"
                                      title={enrollment.course.name}
                                    >
                                      {enrollment.course.name}
                                    </strong>

                                    <span
                                      className={`
                                                                                mt-1.5 inline-flex rounded-full px-2.5 py-1
                                                                                text-[13px] font-semibold
                                                                                ${
                                                                                  courseStatus ===
                                                                                  "Active"
                                                                                    ? "bg-emerald-50 text-emerald-700"
                                                                                    : "bg-slate-100 text-slate-600"
                                                                                }
                                                                            `}
                                    >
                                      {courseStatus}
                                    </span>
                                  </div>
                                </td>

                                <td className="px-4 py-4 align-middle">
                                  <div className="min-w-[100px]">
                                    <span
                                      className={`inline-flex rounded-full px-2.5 py-1 text-[13px] font-semibold ${paymentClass}`}
                                    >
                                      {paymentLabel}
                                    </span>

                                    {enrollment.remaining > 0 && (
                                      <div className="mt-1.5 text-[13px] text-slate-500">
                                        Due:{" "}
                                        <span className="font-semibold text-slate-600">
                                          {enrollment.remaining}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </td>

                                <td className="px-4 py-4 align-middle">
                                  <div className="min-w-[110px] text-[14px] font-medium text-slate-700">
                                    {enrollment.enrollDate.slice(0, 10)}
                                  </div>
                                  <div
                                    className="mt-1 max-w-[140px] truncate text-[13px] text-slate-500"
                                    title={student.createdByName || undefined}
                                  >
                                    {student.createdByName || "—"}
                                  </div>
                                </td>

                                <td className="px-4 py-4 align-middle">
                                  <button
                                    type="button"
                                    disabled={!accessDate}
                                    title={
                                      accessDate
                                        ? "View enrollment logs"
                                        : undefined
                                    }
                                    onClick={() =>
                                      accessDate &&
                                      handleShowLogs(
                                        student.id,
                                        enrollment.id,
                                        enrollment.course.name,
                                      )
                                    }
                                    className="flex min-w-[115px] items-center gap-1.5 border-0 bg-transparent p-0 text-left text-[14px] font-medium text-slate-700 disabled:cursor-default"
                                  >
                                    <span>{accessDate || "—"}</span>
                                    {accessDate && (
                                      <ExternalLink
                                        size={12}
                                        className="text-slate-400"
                                      />
                                    )}
                                  </button>

                                  <span
                                    className={`mt-1.5 inline-flex rounded-full px-2.5 py-1 text-[13px] font-semibold ${accessClass}`}
                                  >
                                    {accessLabel}
                                  </span>
                                </td>

                                <td className="border-r border-r-slate-300 px-4 py-4 text-center align-middle">
                                  <div className="inline-flex items-center justify-center gap-1.5">
                                    {enrollmentIndex === 0 && (
                                      <>
                                        <button
                                          type="button"
                                          title="View student details"
                                          onClick={() =>
                                            nav(`/students/${student.id}`)
                                          }
                                          className={iconButtonClass}
                                        >
                                          <Eye size={15} />
                                        </button>

                                        <button
                                          type="button"
                                          title="Add new enrollment"
                                          onClick={() =>
                                            setEditTarget({
                                              student,
                                              initialStep: 2,
                                            })
                                          }
                                          className={iconButtonClass}
                                        >
                                          <Plus size={15} />
                                        </button>

                                        {canModifyEnrollments &&
                                          !student.mobileAccountStatus && (
                                            <button
                                              type="button"
                                              title="Activate mobile account"
                                              aria-label={`Activate mobile account for ${student.name}`}
                                              disabled={actionLoading}
                                              onClick={() =>
                                                handleActivateMobileAccount(
                                                  student,
                                                )
                                              }
                                              className={iconButtonClass}
                                            >
                                              <Smartphone size={15} />
                                            </button>
                                          )}
                                      </>
                                    )}

                                    <button
                                      type="button"
                                      aria-label="More actions"
                                      title="More actions"
                                      onClick={(event) =>
                                        openMoreActions(
                                          event,
                                          student.id,
                                          enrollment.id,
                                          enrollmentIndex === 0,
                                        )
                                      }
                                      className={iconButtonClass}
                                    >
                                      <MoreVertical size={16} />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          });
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  <div className="flex items-center justify-between gap-4 border-t border-slate-100 px-5 py-3 max-sm:flex-col max-sm:items-stretch max-sm:px-4">
                    <div className="flex items-center gap-2 text-[13px] text-slate-600">
                      <span>Rows per page</span>
                      <select
                        value={rowsPerPage}
                        onChange={(e) => {
                          setRowsPerPage(Number(e.target.value));
                          setCurrentPage(1);
                        }}
                        className="
                                                    h-8 rounded-lg border border-slate-200 bg-white px-2
                                                    text-[13px] font-medium text-slate-700 outline-none
                                                    focus:border-slate-400 focus:ring-2 focus:ring-slate-100
                                                "
                      >
                        {[5, 10, 15].map((count) => (
                          <option key={count} value={count}>
                            {count}
                          </option>
                        ))}
                      </select>
                      <span>
                        {startIndex + 1}–
                        {Math.min(
                          startIndex + rowsPerPage,
                          filteredStudents.length,
                        )}{" "}
                        of {filteredStudents.length}
                      </span>
                    </div>

                    {filteredStudents.length > rowsPerPage && (
                      <div className="flex flex-wrap items-center justify-end gap-1.5 max-sm:justify-start">
                        <button
                          type="button"
                          onClick={() =>
                            setCurrentPage((page) => Math.max(page - 1, 1))
                          }
                          disabled={currentPage === 1}
                          className="
                                                        h-8 rounded-lg border border-emerald-200 bg-white px-3
                                                        text-[13px] font-semibold text-emerald-700 transition
                                                        hover:border-emerald-300 hover:bg-emerald-50
                                                        disabled:cursor-not-allowed disabled:opacity-40
                                                    "
                        >
                          Prev
                        </button>

                        {generatePaginationItems(currentPage, totalPages).map(
                          (item, index) =>
                            item.type === "ellipsis" ? (
                              <span
                                key={`ellipsis-${index}`}
                                className="inline-flex h-8 min-w-8 items-center justify-center text-[13px] font-semibold text-slate-500"
                              >
                                …
                              </span>
                            ) : (
                              <button
                                type="button"
                                key={item.page}
                                onClick={() => setCurrentPage(item.page!)}
                                className={`
                                                                h-8 min-w-8 rounded-lg border px-2 text-[13px]
                                                                font-semibold transition
                                                                ${
                                                                  item.isActive
                                                                    ? "border-emerald-600 bg-emerald-600 text-white"
                                                                    : "border-emerald-200 bg-white text-emerald-700 hover:border-emerald-300 hover:bg-emerald-50"
                                                                }
                                                            `}
                              >
                                {item.page}
                              </button>
                            ),
                        )}

                        <button
                          type="button"
                          onClick={() =>
                            setCurrentPage((page) =>
                              Math.min(page + 1, totalPages),
                            )
                          }
                          disabled={currentPage === totalPages}
                          className="
                                                        h-8 rounded-lg border border-emerald-200 bg-white px-3
                                                        text-[13px] font-semibold text-emerald-700 transition
                                                        hover:border-emerald-300 hover:bg-emerald-50
                                                        disabled:cursor-not-allowed disabled:opacity-40
                                                    "
                        >
                          Next
                        </button>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="flex min-h-[230px] flex-col items-center justify-center px-6 text-center">
                  <div className="grid h-11 w-11 place-items-center rounded-xl bg-slate-100 text-slate-400">
                    <UsersIcon size={20} />
                  </div>
                  <h3 className="mb-0 mt-3 text-[13px] font-semibold text-slate-900">
                    No students found
                  </h3>
                  <p className="mb-0 mt-1 max-w-[430px] text-[10px] leading-5 text-slate-500">
                    Try changing your search or filters to see more students.
                  </p>
                </div>
              )}
            </section>
          )}

          {openActionMenu && actionMenuStudent && actionMenuEnrollment && (
            <>
              <button
                type="button"
                aria-label="Close action menu"
                className="fixed inset-0 z-[1390] cursor-default bg-transparent"
                onClick={() => setOpenActionMenu(null)}
              />

              <div
                role="menu"
                className="fixed z-[1400] w-[244px] overflow-hidden rounded-[14px] border border-slate-200 bg-white p-2 shadow-[0_18px_50px_rgba(15,23,42,0.16)]"
                style={{ top: openActionMenu.top, left: openActionMenu.left }}
              >
                <div className="border-b border-slate-100 px-2.5 pb-2.5 pt-1.5">
                  <div className="truncate text-[15px] font-semibold text-slate-900">
                    {actionMenuStudent.name}
                  </div>
                  <div className="mt-0.5 truncate text-[13px] text-slate-500">
                    {actionMenuEnrollment.course.name}
                  </div>
                </div>

                <div className="py-1.5">
                  <button
                    type="button"
                    role="menuitem"
                    disabled={actionLoading || !actionMenuEnrollment.inviteLink}
                    onClick={() => {
                      if (!actionMenuEnrollment.inviteLink) return;
                      navigator.clipboard.writeText(
                        actionMenuEnrollment.inviteLink,
                      );
                      setOpenActionMenu(null);
                      alert("Invite link copied!");
                    }}
                    className={menuItemClass}
                  >
                    <Copy size={16} className="shrink-0 text-slate-400" />
                    Copy invite link
                  </button>

                  {canResendEmail(actionMenuStudent) &&
                    (actionMenuEnrollment.status === "access_granted" ||
                      actionMenuEnrollment.status === "grace_used") && (
                      <>
                        <button
                          type="button"
                          role="menuitem"
                          disabled={actionLoading}
                          onClick={() => {
                            setOpenActionMenu(null);
                            handleResendEmail(
                              actionMenuStudent.id,
                              actionMenuEnrollment.id,
                            );
                          }}
                          className={menuItemClass}
                        >
                          <Mail size={16} className="shrink-0 text-slate-400" />
                          Resend access email
                        </button>

                        <button
                          type="button"
                          role="menuitem"
                          disabled={actionLoading}
                          onClick={() => {
                            setOpenActionMenu(null);
                            handleResendDrive(
                              actionMenuStudent.id,
                              actionMenuEnrollment.id,
                            );
                          }}
                          className={menuItemClass}
                        >
                          <HardDrive
                            size={16}
                            className="shrink-0 text-slate-400"
                          />
                          Retrigger Drive access
                        </button>
                      </>
                    )}

                  {canModifyEnrollments &&
                    actionMenuEnrollment.status !== "grace_used" && (
                      <button
                        type="button"
                        role="menuitem"
                        disabled={
                          actionMenuEnrollment.isFullyPaid || actionLoading
                        }
                        onClick={() => {
                          setOpenActionMenu(null);
                          handleGrantGraceClick(
                            actionMenuStudent.id,
                            actionMenuEnrollment.id,
                          );
                        }}
                        className={menuItemSuccessClass}
                      >
                        <Gift size={16} className="shrink-0" />
                        Grant grace period
                      </button>
                    )}

                  {canModifyEnrollments &&
                    actionMenuEnrollment.status !== "grace_used" &&
                    actionMenuEnrollment.status !== "banned" &&
                    actionMenuEnrollment.status !== "no_access" && (
                      <button
                        type="button"
                        role="menuitem"
                        disabled={actionLoading}
                        onClick={() => {
                          setOpenActionMenu(null);
                          handleBanClick(
                            actionMenuStudent.id,
                            actionMenuEnrollment.id,
                          );
                        }}
                        className={menuItemDangerClass}
                      >
                        <Ban size={16} className="shrink-0" />
                        Ban enrollment
                      </button>
                    )}

                  {canModifyEnrollments &&
                    actionMenuEnrollment.status === "grace_used" && (
                      <button
                        type="button"
                        role="menuitem"
                        disabled={actionLoading}
                        onClick={() => {
                          setOpenActionMenu(null);
                          handleRevokeGraceClick(
                            actionMenuStudent.id,
                            actionMenuEnrollment.id,
                          );
                        }}
                        className={menuItemWarningClass}
                      >
                        <XCircle size={16} className="shrink-0" />
                        Revoke grace period
                      </button>
                    )}

                  {canModifyEnrollments &&
                    actionMenuEnrollment.status === "banned" && (
                      <button
                        type="button"
                        role="menuitem"
                        disabled={actionLoading}
                        onClick={() => {
                          setOpenActionMenu(null);
                          handleUnbanClick(
                            actionMenuStudent.id,
                            actionMenuEnrollment.id,
                          );
                        }}
                        className={menuItemSuccessClass}
                      >
                        <Check size={16} className="shrink-0" />
                        Unban enrollment
                      </button>
                    )}

                  {actionMenuEnrollment.remaining > 0 && (
                    <button
                      type="button"
                      role="menuitem"
                      disabled={actionLoading}
                      onClick={() => {
                        setOpenActionMenu(null);
                        setPaymentTarget({
                          studentId: actionMenuStudent.id,
                          courseId: actionMenuEnrollment.course.id,
                          remaining: actionMenuEnrollment.remaining,
                        });
                      }}
                      className={menuItemClass}
                    >
                      <IndianRupee
                        size={16}
                        className="shrink-0 text-slate-400"
                      />
                      Make a payment
                    </button>
                  )}

                  {actionMenuHasMultipleEnrollments && canModifyEnrollments && (
                    <button
                      type="button"
                      role="menuitem"
                      disabled={actionLoading}
                      onClick={() => {
                        setOpenActionMenu(null);
                        handleRemoveEnrollment(
                          actionMenuStudent.id,
                          actionMenuEnrollment.id,
                        );
                      }}
                      className={menuItemDangerClass}
                    >
                      <Trash2 size={16} className="shrink-0" />
                      Remove enrollment
                    </button>
                  )}

                  {canEdit && (
                    <button
                      type="button"
                      role="menuitem"
                      disabled={actionLoading}
                      onClick={() => {
                        setOpenActionMenu(null);
                        setEditTarget({ student: actionMenuStudent });
                      }}
                      className={menuItemClass}
                    >
                      <Pencil size={16} className="shrink-0 text-slate-400" />
                      Edit student
                    </button>
                  )}

                  {openActionMenu.isPrimaryRow &&
                    canDeleteStudent(actionMenuStudent) && (
                      <>
                        <div className="my-1 border-t border-slate-100" />
                        <button
                          type="button"
                          role="menuitem"
                          disabled={actionLoading}
                          onClick={() => {
                            setOpenActionMenu(null);
                            handleDeleteStudent(actionMenuStudent.id);
                          }}
                          className={menuItemDangerClass}
                        >
                          <Trash2 size={16} className="shrink-0" />
                          Delete student
                        </button>
                      </>
                    )}
                </div>
              </div>
            </>
          )}

          {/* Modals */}
          {showMobileBulk && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/45 p-4" role="dialog" aria-modal="true" aria-labelledby="mobile-bulk-title">
              <div className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white shadow-2xl">
                <div className="flex items-start justify-between border-b border-slate-100 px-6 py-5">
                  <div>
                    <h2 id="mobile-bulk-title" className="m-0 text-lg font-bold text-slate-950">Send mobile activation emails</h2>
                    <p className="mb-0 mt-1 text-xs text-slate-500">Includes students without a mobile account and unactivated students whose previous OTP has expired. A new OTP is never sent before the 72-hour expiry.</p>
                  </div>
                  <button type="button" aria-label="Close" onClick={() => setShowMobileBulk(false)} className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700"><X size={18} /></button>
                </div>

                <div className="space-y-4 px-6 py-5">
                  <label className="block">
                    <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">Course</span>
                    <select value={mobileBulkCourseId} onChange={(event) => loadMobileBulkPreview(event.target.value)} className={fieldClass}>
                      <option value="">Select a course</option>
                      {coursesOptions.map(course => <option key={course.id} value={course.id}>{course.name}</option>)}
                    </select>
                  </label>

                  {mobileBulkLoading && !mobileBulkPreview && <div className="rounded-xl bg-slate-50 p-4 text-sm text-slate-600">Checking eligible students…</div>}
                  {mobileBulkError && <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">{mobileBulkError}</div>}
                  {mobileBulkPreview && (
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="rounded-xl bg-emerald-50 p-4"><div className="text-2xl font-bold text-emerald-700">{mobileBulkPreview.willSend}</div><div className="mt-1 text-xs text-emerald-800">will receive email now</div></div>
                        <div className="rounded-xl bg-slate-50 p-4"><div className="text-2xl font-bold text-slate-800">{mobileBulkPreview.remainingToday}</div><div className="mt-1 text-xs text-slate-600">safe sends remaining today</div></div>
                      </div>
                      <div className="rounded-xl border border-slate-200 p-4 text-xs leading-6 text-slate-600">
                        <div>{mobileBulkPreview.eligibleStudents} eligible · {mobileBulkPreview.expiredPendingStudents} need a replacement OTP</div>
                        <div>{mobileBulkPreview.pendingWaitingStudents} pending with an OTP still inside its 72-hour window</div>
                        <div>{mobileBulkPreview.activeStudents} already active</div>
                        <div>{mobileBulkPreview.ineligibleAccessStudents} without granted/grace access</div>
                        <div>{mobileBulkPreview.disabledStudents} disabled · {mobileBulkPreview.invalidEmailStudents} invalid email</div>
                        <div>{mobileBulkPreview.alreadyAttemptedToday} already handled today</div>
                      </div>
                      <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                        Daily allowance resets in <strong>{mobileResetCountdown}</strong> at midnight Nepal time. The system allows at most {mobileBulkPreview.dailyLimit} bulk activation emails per course and {mobileBulkPreview.dailyLimit} total per day.
                      </div>
                      <label className="flex items-start gap-2 text-xs text-slate-600">
                        <input type="checkbox" className="mt-0.5" id="mobile-bulk-confirm" onChange={(event) => event.currentTarget.dataset.checked = String(event.currentTarget.checked)} />
                        <span>I confirm that activation emails should be sent to the eligible students shown above.</span>
                      </label>
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-2 border-t border-slate-100 px-6 py-4">
                  <button type="button" onClick={() => setShowMobileBulk(false)} className="h-10 rounded-lg border border-slate-200 px-4 text-xs font-semibold text-slate-600 hover:bg-slate-50">Cancel</button>
                  <button type="button" disabled={mobileBulkLoading || !mobileBulkPreview?.willSend} onClick={() => {
                    const checked = (document.getElementById('mobile-bulk-confirm') as HTMLInputElement | null)?.checked;
                    if (!checked) { setMobileBulkError("Please tick the confirmation box first."); return; }
                    handleMobileBulkSend();
                  }} className="h-10 rounded-lg bg-emerald-600 px-4 text-xs font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50">
                    {mobileBulkLoading ? "Sending…" : `Send ${mobileBulkPreview?.willSend || 0} emails`}
                  </button>
                </div>
              </div>
            </div>
          )}
          {showNew && (
            <StudentForm
              onSubmit={handleNew}
              onCancel={() => setShowNew(false)}
            />
          )}

          {editTarget && (
            <EditStudentForm
              student={editTarget.student}
              initialStep={editTarget.initialStep}
              onSubmit={handleEdit}
              onRemoveEnrollment={async (id, enrId) => {
                setActionLoading(true);
                try {
                  await removeEnrollment(id, enrId);
                  await reload();
                } finally {
                  setActionLoading(false);
                }
              }}
              onAddEnrollment={async (id, enrollment) => {
                setActionLoading(true);
                try {
                  await addEnrollment(id, enrollment);
                  await reload();
                } finally {
                  setActionLoading(false);
                }
              }}
              onCancel={() => setEditTarget(null)}
            />
          )}

          {logModal && (
            <EnrollmentLogsModal
              isOpen
              studentId={logModal.studentId}
              enrollmentId={logModal.enrollmentId}
              courseName={logModal.courseName}
              onClose={() => setLogModal(null)}
            />
          )}

          {paymentTarget && (
            <PaymentForm
              onSubmit={handlePaymentSubmit}
              onCancel={() => setPaymentTarget(null)}
              initialStudentId={paymentTarget.studentId}
              initialCourseId={paymentTarget.courseId}
            />
          )}

          {actionModal &&
            (() => {
              const student = students.find(
                (s) => s.id === actionModal.studentId,
              )!;
              const modalEnroll = student.enrollments.find(
                (en) => en.id === modalEnrollmentId,
              );

              return (
                <ActionModal
                  isOpen
                  type={actionModal.type}
                  courses={
                    modalEnroll
                      ? [{ id: modalEnroll.id, name: modalEnroll.course.name }]
                      : []
                  }
                  defaultDays={modalGraceDays}
                  onCancel={() => setActionModal(null)}
                  onConfirm={(_, days) => confirmAction(days)}
                />
              );
            })()}
        </div>
      </main>
    </div>
  );
}
